import type { DesignDocument, FaceKey, SizeMm } from '@repo/api-contracts';
import { create } from 'zustand';
import { findNode, removeNode, reorderNode, updateNode } from './document-ops';
import { type EditorEvent, type EditorMode, transition } from './editor-transitions';
import {
  alignInArea,
  clearDocument,
  type FlipAxis,
  flipImage,
  hasAnyLayer,
  type LayerAlignment,
} from './layer-arrange';

export const HISTORY_LIMIT = 50;

export interface EditorInit {
  readonly document: DesignDocument;
  readonly designId: string | null;
  readonly faceKey: FaceKey;
  readonly componentId: string;
}

interface EditorState {
  readonly document: DesignDocument;
  readonly designId: string | null;
  readonly faceKey: FaceKey;
  readonly componentId: string;
  readonly selectedNodeId: string | null;
  readonly mode: EditorMode;
  readonly revision: number;
  readonly dirty: boolean;
  readonly failure: string | null;
  readonly past: readonly DesignDocument[];
  readonly future: readonly DesignDocument[];
  /** 마지막 기록을 남긴 mutate 의 합치기 키 — 같은 키로 이어진 변경은 기록 하나로 합친다(글자 바로 고치기 한 번 = undo 한 번). */
  readonly mergeKey: string | null;
}

/** mutate 선택값 — mergeKey 가 직전 기록의 키와 같으면 과거를 새로 쌓지 않고 마지막 기록에 합친다. */
export interface MutateOptions {
  readonly mergeKey?: string;
}

interface EditorActions {
  initialize: (init: EditorInit) => void;
  select: (nodeId: string) => void;
  deselect: () => void;
  /** 선택만 푼다 — 모드는 평면 편집에 머문다(목업 위 바로 편집). */
  clearSelection: () => void;
  previewHit: (nodeId: string, componentId: string) => void;
  previewMiss: () => void;
  renderSettled: (revision: number) => void;
  renderFailed: (revision: number, reason: string) => void;
  retryRender: () => void;
  backToEdit: () => void;
  mutate: (fn: (document: DesignDocument) => DesignDocument, options?: MutateOptions) => void;
  setFace: (faceKey: FaceKey, componentId: string) => void;
  setComponent: (componentId: string) => void;
  setColor: (colorOptionId: string) => void;
  undo: () => void;
  redo: () => void;
  markSaved: (designId: string) => void;
  /** 선택한 노드 삭제(그룹 포함). 선택이 없으면 아무 일도 없다. */
  removeSelected: () => void;
  /** 선택한 노드를 같은 부모 안에서 한 칸 앞('up')·뒤('down')로. */
  reorderSelected: (direction: 'up' | 'down') => void;
  /** 선택한 이미지·텍스트를 인쇄 영역(area) 안에서 정렬한다. 그룹은 상자가 없어 그대로다. */
  alignSelected: (alignment: LayerAlignment, area: SizeMm) => void;
  /** 선택한 이미지를 반전한다. 이미지가 아니면 그대로다. */
  flipSelected: (axis: FlipAxis) => void;
  /** 처음으로 — 모든 면의 레이어를 지운다. undo 로 되돌릴 수 있다. 지울 것이 없으면 기록을 남기지 않는다. */
  reset: () => void;
}

const initial = (init: EditorInit): EditorState => ({
  ...init,
  selectedNodeId: null,
  mode: 'flat',
  revision: 0,
  dirty: false,
  failure: null,
  past: [],
  future: [],
  mergeKey: null,
});

const EMPTY: EditorInit = {
  document: { productId: '', colorOptionId: '', components: [] },
  designId: null,
  faceKey: 'front',
  componentId: '',
};

/** 고객 에디터 스토어 — 문서(mm)·선택·모드·revision·undo/redo. 모드 전환은 editor-transitions 가 결정한다. */
export const useEditorStore = create<EditorState & EditorActions>()((set, get) => {
  const step = (event: EditorEvent) => {
    const { mode, revision, selectedNodeId } = get();
    set(transition({ mode, revision, selectedNodeId }, event));
  };
  /** 문서가 바뀐 뒤 선택한 노드가 사라졌으면(삭제·추가의 undo) 선택을 푼다 — 안내선 요청도 선택에 묶여 함께 내려간다. */
  const dropStaleSelection = () => {
    const { document, selectedNodeId } = get();
    if (selectedNodeId !== null && findNode(document, selectedNodeId) === null)
      set({ selectedNodeId: null });
  };
  const replaceDocument = (document: DesignDocument, mergeKey: string | null = null) => {
    const { document: previous, past, mergeKey: lastKey } = get();
    const merging = mergeKey !== null && mergeKey === lastKey && past.length > 0;
    set({
      document,
      past: merging ? past : [...past.slice(-(HISTORY_LIMIT - 1)), previous],
      future: [],
      dirty: true,
      failure: null,
      mergeKey,
    });
    dropStaleSelection();
    step({ type: 'mutate' });
  };
  return {
    ...initial(EMPTY),
    initialize: (init) => set(initial(init)),
    select: (nodeId) => step({ type: 'select', nodeId }),
    deselect: () => step({ type: 'deselect' }),
    clearSelection: () => step({ type: 'clearSelection' }),
    previewHit: (nodeId, componentId) => {
      set({ componentId });
      step({ type: 'previewHit', nodeId });
    },
    previewMiss: () => step({ type: 'previewMiss' }),
    renderSettled: (revision) => step({ type: 'renderSettled', revision }),
    renderFailed: (revision, reason) => {
      if (revision === get().revision) set({ failure: reason });
      step({ type: 'renderFailed', revision });
    },
    retryRender: () => {
      set({ failure: null });
      step({ type: 'retry' });
    },
    backToEdit: () => step({ type: 'backToEdit' }),
    mutate: (fn, options) => replaceDocument(fn(get().document), options?.mergeKey ?? null),
    setFace: (faceKey, componentId) => {
      set({ faceKey, componentId, selectedNodeId: null });
      step({ type: 'mutate' });
    },
    setComponent: (componentId) => set({ componentId, selectedNodeId: null }),
    setColor: (colorOptionId) => replaceDocument({ ...get().document, colorOptionId }),
    undo: () => {
      const { past, future, document } = get();
      const previous = past.at(-1);
      if (previous === undefined) return;
      set({
        document: previous,
        past: past.slice(0, -1),
        future: [document, ...future],
        dirty: true,
        mergeKey: null,
      });
      dropStaleSelection();
      step({ type: 'mutate' });
    },
    redo: () => {
      const { past, future, document } = get();
      const [next, ...rest] = future;
      if (next === undefined) return;
      set({
        document: next,
        past: [...past, document],
        future: rest,
        dirty: true,
        mergeKey: null,
      });
      dropStaleSelection();
      step({ type: 'mutate' });
    },
    markSaved: (designId) => set({ designId, dirty: false }),
    removeSelected: () => {
      const { selectedNodeId } = get();
      if (selectedNodeId !== null) replaceDocument(removeNode(get().document, selectedNodeId));
    },
    reorderSelected: (direction) => {
      const { selectedNodeId } = get();
      if (selectedNodeId !== null)
        replaceDocument(reorderNode(get().document, selectedNodeId, direction));
    },
    alignSelected: (alignment, area) => {
      const { selectedNodeId, document } = get();
      const found = selectedNodeId === null ? null : findNode(document, selectedNodeId);
      if (found === null || found.node.type === 'group') return;
      replaceDocument(
        updateNode(document, found.node.id, (node) =>
          node.type === 'group' ? node : alignInArea(node, area, alignment),
        ),
      );
    },
    flipSelected: (axis) => {
      const { selectedNodeId, document } = get();
      const found = selectedNodeId === null ? null : findNode(document, selectedNodeId);
      if (found?.node.type !== 'image') return;
      replaceDocument(
        updateNode(document, found.node.id, (node) =>
          node.type === 'image' ? flipImage(node, axis) : node,
        ),
      );
    },
    reset: () => {
      const { document } = get();
      if (!hasAnyLayer(document)) return;
      replaceDocument(clearDocument(document));
    },
  };
});
